import type ImageType from "@travily/types/shared/Image";
import { Card, CardDescription, CardFavoriteButton, CardImage, CardTitle } from "@ui/components/Card";
import { Carousel } from "@ui/components/Carousel";
import { HoverCard } from "@ui/components/HoverCard";
import { Rating } from "@ui/components/Rating";
import { tv } from "@ui/lib/tv";
import { Link } from "@ui/primitives/Link";
import type { CardTheme } from "@ui/types/card";
import type { ReactNode } from "react";
import { AccountAvatar } from "@/components/AccountAvatar/AccountAvatar";

const listingCard = tv({
  slots: {
    root: "group h-full w-full",
    image: "rounded-xl md:rounded-3xl",
    content: "flex flex-1 flex-col gap-2",
    header: "flex flex-col gap-1",
    body: "flex flex-1 flex-col gap-2",
    priceRating: "mt-auto flex items-center gap-2.5",
    price: "text-neutral-900",
    dot: "size-[3px] shrink-0 rounded-full bg-neutral-900",
    authorLink: "relative z-10 flex min-w-0 items-center gap-2 no-underline",
    authorName: "line-clamp-1 min-w-0 flex-1 leading-6 text-neutral-600",
  },
  variants: {
    theme: {
      editorial: {
        price: "text-base font-semibold md:text-lg",
        authorName: "text-base font-normal",
      },
      compact: {
        price: "text-[15px] font-medium leading-none tracking-[-0.005em]",
        authorName: "text-base font-medium",
      },
    },
  },
  defaultVariants: {
    theme: "editorial",
  },
});

export interface ListingCardAuthor {
  profile_type: string;
  avatar?: ImageType | null;
  href: string;
  initial: string;
  name: string;
}

export interface ListingCardRating {
  count: number;
  score: number;
}

export interface ListingCardProps {
  author?: ListingCardAuthor;
  className?: string;
  description?: string | null;
  favorited?: boolean;
  href: string;
  image?: ImageType | null;
  /** Multiple images — renders a Carousel when length > 1. */
  images?: ImageType[];
  linkLabel: string;
  onFavoriteToggle?: () => void;
  overlayTopLeft?: ReactNode;
  price?: string | null;
  priceApproximate?: boolean;
  priceHoverContent?: ReactNode;
  rating?: ListingCardRating;
  /** Visual treatment: "editorial" for spacious layouts, "compact" for dense grids. */
  theme?: CardTheme;
  title: string;
}

export function ListingCard({
  author,
  className,
  description,
  favorited,
  href,
  image,
  images,
  linkLabel,
  onFavoriteToggle,
  overlayTopLeft,
  price,
  priceApproximate,
  priceHoverContent,
  rating,
  theme = "editorial",
  title,
}: ListingCardProps) {
  const styles = listingCard({ theme });
  const hasRating = rating != null;
  const showCarousel = images != null && images.length > 1;

  const heartOverlay = onFavoriteToggle ? (
    <CardFavoriteButton favorited={favorited} name={title} onToggle={onFavoriteToggle} />
  ) : undefined;

  return (
    <div className={styles.root({ className })}>
      <Card className="h-full gap-3 md:gap-2.5" href={href} linkLabel={linkLabel}>
        {showCarousel ? (
          <div className={`relative overflow-hidden ${styles.image()}`}>
            <Carousel
              aspect="square"
              controls="hover"
              images={images}
              inset="sm"
              mobile="carousel"
              size="compact"
              sizes="(min-width: 1280px) 25vw, (min-width: 768px) 33vw, 100vw"
            />
            {overlayTopLeft ? <div className="absolute left-4 top-4 z-20">{overlayTopLeft}</div> : null}
            {heartOverlay ? <div className="absolute right-4 top-4 z-20">{heartOverlay}</div> : null}
          </div>
        ) : (
          <CardImage
            alt={title}
            className={styles.image()}
            image={image}
            overlayInset="sm"
            overlayTopLeft={overlayTopLeft}
            overlayTopRight={heartOverlay}
          />
        )}
        <div className={styles.content()}>
          <div className={styles.header()}>
            <CardTitle theme={theme}>{title}</CardTitle>
            {author ? (
              <Link className={styles.authorLink()} href={author.href}>
                <AccountAvatar account={author} shape="circle" size="24" />
                <span className={styles.authorName()}>{author.name}</span>
              </Link>
            ) : null}
          </div>
          <div className={styles.body()}>
            {description ? <CardDescription theme={theme}>{description}</CardDescription> : null}
            {(price || hasRating) && (
              <div className={styles.priceRating()}>
                {price && priceApproximate && priceHoverContent ? (
                  <HoverCard content={priceHoverContent} placement="top" triggerClassName="relative z-10 cursor-help">
                    <p className={`${styles.price()} border-b border-neutral-900 leading-tight`}>{price}</p>
                  </HoverCard>
                ) : (
                  price && <p className={styles.price()}>{price}</p>
                )}
                {price && hasRating && <div className={styles.dot()} />}
                {hasRating && theme === "compact" ? (
                  <Rating reviewCount={rating.count} score={rating.score} size="20" variant="compact" />
                ) : hasRating ? (
                  <>
                    <Rating
                      className="md:hidden"
                      reviewCount={rating.count}
                      score={rating.score}
                      size="20"
                      variant="compact"
                    />
                    <Rating
                      className="hidden md:flex"
                      reviewCount={rating.count}
                      score={rating.score}
                      size="24"
                      variant="compact"
                    />
                  </>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
